import { Controller } from "@hotwired/stimulus"
import { badgeClasses } from '@/utils/badge_classes'

export default class extends Controller {
  static targets = ["trigger"]
  static values = { settingUrl: String }
  static outlets = ["team-playbook--task-details"]

  connect() {
    this.#refreshHandler = () => window.location.reload()
    document.addEventListener("team-playbook:frequencies-changed", this.#refreshHandler)
  }

  disconnect() {
    document.removeEventListener("team-playbook:frequencies-changed", this.#refreshHandler)
  }

  selectStatic(event) {
    event.preventDefault()
    const { frequencyName, frequencyColor } = event.currentTarget.dataset
    this.#updateTo({
      name: frequencyName,
      color: frequencyColor,
      params: { frequency: frequencyName, custom_frequency_id: "" }
    })
  }

  selectCustom(event) {
    event.preventDefault()
    const { frequencyId, frequencyName, frequencyColor } = event.currentTarget.dataset
    this.#updateTo({
      name: frequencyName,
      color: frequencyColor,
      params: { custom_frequency_id: frequencyId, frequency: "" }
    })
  }

  clear(event) {
    event.preventDefault()
    if (!window.confirm("Clear frequency for this row?")) return

    this.#updateTo({
      name: null,
      color: null,
      params: { frequency: "", custom_frequency_id: "" }
    })
  }

  #updateTo({ name, color, params }) {
    const previousTriggerHTML = this.triggerTarget.innerHTML

    this.triggerTarget.innerHTML = name
      ? this.#badgeHTML(name, color)
      : `<span class="${badgeClasses({ variant: 'secondary' })}">N/A</span>`

    // Optimistically propagate to any open sub-rows. Server-side
    // SettingsController#propagate_frequency_to_group! does the same on the
    // backend, so a refetch would land on identical state.
    const taskDetails = this.#taskDetailsOrNull
    const subRowsSnapshot = taskDetails?.snapshotRelatedFrequencies() ?? null
    taskDetails?.setRelatedFrequency({ name, color })

    this.#persistSetting(params, previousTriggerHTML, subRowsSnapshot)
  }

  #badgeHTML(name, color) {
    return `<span class="${badgeClasses({ variant: null })}" style="background-color: ${color}">${name}</span>`
  }

  #persistSetting(params, previousTriggerHTML, subRowsSnapshot) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""

    fetch(this.settingUrlValue, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
      body: JSON.stringify({ team_playbook_setting: params }),
    }).then((response) => {
      if (!response.ok) {
        this.#revert(previousTriggerHTML, subRowsSnapshot)
        console.error(`[TeamPlaybook] Failed to update frequency: ${response.status}`)
      }
    }).catch((error) => {
      this.#revert(previousTriggerHTML, subRowsSnapshot)
      console.error("[TeamPlaybook] Failed to update frequency:", error)
    })
  }

  #revert(previousTriggerHTML, subRowsSnapshot) {
    if (previousTriggerHTML) this.triggerTarget.innerHTML = previousTriggerHTML
    if (subRowsSnapshot) this.#taskDetailsOrNull?.restoreRelatedFrequencies(subRowsSnapshot)
  }

  get #taskDetailsOrNull() {
    return this.hasTeamPlaybookTaskDetailsOutlet ? this.teamPlaybookTaskDetailsOutlet : null
  }

  #refreshHandler = null
}
